import type { ReactElement, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CookieConsentModal, useCookieConsent } from '@fetmode/app-core';
import { useLocation } from 'react-router-dom';

import { useAuth } from '../auth/index.js';
import { AgeEntryGate } from './AgeEntryGate.js';
import { getAgeEntryStatus, setAgeEntryAccepted } from './age-entry-storage.js';

export interface EntryComplianceLayerProps {
  children: ReactNode;
}

/** Legal pages users must be able to read before accepting cookies. */
const COOKIE_GATE_EXEMPT_PATHS = new Set(['/p/cookies', '/p/privacy', '/p/terms']);

export function EntryComplianceLayer({ children }: EntryComplianceLayerProps): ReactElement {
  const { api, user, isLoading: authLoading } = useAuth();
  const [ageAccepted, setAgeAccepted] = useState(() => getAgeEntryStatus() === 'accepted');
  const { choice: cookieChoice, preferencesOpen } = useCookieConsent();
  const location = useLocation();
  const cookieGateExempt = COOKIE_GATE_EXEMPT_PATHS.has(location.pathname);
  const syncingRef = useRef(false);

  useEffect(() => {
    if (authLoading || !user || syncingRef.current) {
      return;
    }

    let cancelled = false;
    syncingRef.current = true;

    void (async () => {
      try {
        const status = await api.getComplianceStatus();
        if (cancelled) {
          return;
        }
        if (status.entryAccepted) {
          setAgeEntryAccepted();
          setAgeAccepted(true);
          return;
        }
        if (getAgeEntryStatus() === 'accepted') {
          await api.acknowledgeAgeEntry();
          if (!cancelled) {
            setAgeAccepted(true);
          }
        }
      } catch {
        // Keep local gate state; server sync retries on next session.
      } finally {
        syncingRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
      syncingRef.current = false;
    };
  }, [api, authLoading, user]);

  const handleAgeAccepted = useCallback(() => {
    setAgeEntryAccepted();
    setAgeAccepted(true);
    if (user) {
      void api.acknowledgeAgeEntry().catch(() => {
        // Local accept remains; sync retries after login/refresh.
      });
    }
  }, [api, user]);

  if (!ageAccepted) {
    return <AgeEntryGate onAccepted={handleAgeAccepted} />;
  }

  // First visit: block the app until they choose — except cookie/privacy/terms
  // so the policy link inside the dialog is actually readable.
  if (cookieChoice === null && !cookieGateExempt) {
    return <CookieConsentModal />;
  }

  // Re-opened from footer, or reading an exempt page before choosing: keep the
  // app mounted and overlay the dialog when preferences are open / undecided.
  if (preferencesOpen || (cookieChoice === null && cookieGateExempt)) {
    return (
      <>
        {children}
        <CookieConsentModal />
      </>
    );
  }

  return <>{children}</>;
}
